import type { FC, ReactNode } from 'react'
import type { SellerListing } from '@/types/marketplace'

import Link from 'next/link'
import {
    Clock,
    Eye,
    Globe,
    MapPin,
    Send
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { listingTypeLabel } from '@/lib/marketplace/format'
import { CategoryIcon } from '@/components/marketplace/category-icon'
import { ListingPlaceholderArt } from '@/components/marketplace/listing-placeholder-art'
import { ListingLikeCount } from '@/components/marketplace/listing-like-count'
import { SaveListingButton } from '@/components/featured-listings/save-listing-button'

const isRemote = (location: string): boolean => /online|remote|worldwide|digital/i.test(location)

interface SellerListingCardProps {
    listing: SellerListing
    /** The first row on the page can be the LCP image. */
    priority?: boolean
    className?: string
}

/**
 * The wide listing row used ONLY on a seller profile.
 *
 * The shared grid card (`listing-card.tsx`) stays the one card for every
 * marketplace GRID. A profile lists a seller's own stock as a full-width row
 * instead, so it can carry the blurb, the delivery marks and the view count
 * that a grid card has no room for. The whole row is one link: the title
 * carries it and its pseudo-element covers the row, so the save control stays
 * a real button rather than a control nested inside an anchor.
 */
export const SellerListingCard: FC<SellerListingCardProps> = ({ listing, priority, className }): ReactNode => {
    const place = listing.location?.trim() || ''
    const remote = Boolean(place) && isRemote(place)
    const LocationIcon = remote ? Globe : MapPin
    const sold = listing.status === 'sold'

    return (
        <article
            className={cn(
                'group relative flex flex-col gap-5 overflow-hidden rounded-mta-md border border-ink-900/15 bg-paper p-4 sm:flex-row sm:gap-6 sm:p-5',
                'transition-[border-color,box-shadow] duration-[var(--dur)] ease-[var(--ease-brand)]',
                'focus-within:border-ink-900 hover:border-ink-900/45',
                className
            )}
        >
            <div className='relative shrink-0 overflow-hidden rounded-mta-sm bg-plate sm:size-[190px]'>
                {listing.imageUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                        src={listing.imageUrl}
                        alt={listing.title}
                        width={520}
                        height={520}
                        loading={priority ? 'eager' : 'lazy'}
                        fetchPriority={priority ? 'high' : 'auto'}
                        decoding='async'
                        sizes='(min-width: 640px) 190px, 92vw'
                        className={cn(
                            'h-44 w-full object-cover transition-transform duration-500 ease-out sm:h-full',
                            'motion-safe:group-hover:scale-[1.03]',
                            sold && 'opacity-45'
                        )}
                    />
                ) : (
                    <ListingPlaceholderArt
                        placeholder={listing.placeholder}
                        categorySlug={listing.categorySlug}
                        seed={listing.id}
                        className='h-44 sm:h-full'
                        iconClassName='size-11'
                    />
                )}

                <span className='type-label mta-border absolute left-2.5 top-2.5 rounded-mta-xs bg-paper px-2 py-1 text-[10px] leading-none text-ink-900'>
                    {sold ? 'Sold' : listingTypeLabel(listing.listingType)}
                </span>

                <SaveListingButton
                    listingId={listing.id}
                    title={listing.title}
                    className='absolute bottom-2.5 right-2.5 z-10'
                />
            </div>

            <div className='min-w-0 flex-1'>
                {listing.categoryName && (
                    <p className='type-eyebrow flex items-center gap-1.5 text-brand-red-ink'>
                        <CategoryIcon name={listing.categoryIcon} className='size-3.5' />
                        {listing.categoryName}
                    </p>
                )}

                <h3 className='mt-2 text-lg font-bold leading-snug text-ink-900 sm:text-xl'>
                    <Link
                        href={`/listing/${listing.slug}`}
                        className='mta-focus mta-scroll-safe rounded-mta-xs after:absolute after:inset-0 after:content-[""]'
                    >
                        {listing.title}
                    </Link>
                </h3>

                {listing.description && (
                    <p className='mt-2 line-clamp-2 text-sm leading-relaxed text-text-muted'>
                        {listing.description}
                    </p>
                )}

                <p className='type-display mt-4 text-3xl leading-none text-ink-900'>
                    {listing.priceLabel}
                </p>

                {(place || remote) && (
                    <div className='mt-4 flex flex-wrap items-center gap-2'>
                        {place && (
                            <span className='type-label mta-border inline-flex items-center gap-1.5 rounded-mta-xs bg-paper px-2.5 py-1.5 text-[10px] leading-none text-ink-900'>
                                <LocationIcon className='size-3.5' aria-hidden='true' />
                                {place}
                            </span>
                        )}

                        {remote && (
                            <span className='type-label mta-border inline-flex items-center gap-1.5 rounded-mta-xs bg-surface-muted px-2.5 py-1.5 text-[10px] leading-none text-ink-900'>
                                <Send className='size-3.5' aria-hidden='true' />
                                Digital delivery
                            </span>
                        )}
                    </div>
                )}

                <div className='mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-text-muted'>
                    <span className='inline-flex items-center gap-1.5'>
                        <Clock className='size-3.5' aria-hidden='true' />
                        {`Listed ${listing.postedLabel.toLowerCase()}`}
                    </span>

                    <span aria-hidden='true' className='h-3 w-px bg-ink-900/20' />

                    <span className='inline-flex items-center gap-1.5'>
                        <Eye className='size-3.5' aria-hidden='true' />
                        {`${listing.viewCount} ${listing.viewCount === 1 ? 'view' : 'views'}`}
                    </span>

                    <span aria-hidden='true' className='h-3 w-px bg-ink-900/20' />

                    <ListingLikeCount listingId={listing.id} count={listing.favoriteCount} />
                </div>
            </div>
        </article>
    )
}
